/**
 * Date math for the month / week / day calendar. Everything works on LOCAL
 * calendar days keyed 'YYYY-MM-DD' (the browser's zone), so a timed event
 * lands on the day the owner sees it, and DST never shifts a cell.
 */

export type CalView = 'month' | 'week' | 'day';

export const pad = (n: number): string => String(n).padStart(2, '0');

/** 'YYYY-MM-DD' of a Date in the browser's zone. */
export function dayKey(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Local midnight of a 'YYYY-MM-DD'. */
export function parseDay(key: string): Date {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1);
}

export function addDays(key: string, n: number): string {
  const d = parseDay(key);
  d.setDate(d.getDate() + n);
  return dayKey(d);
}

export function addMonths(key: string, n: number): string {
  const d = parseDay(key);
  const target = new Date(d.getFullYear(), d.getMonth() + n, 1);
  // Keep the day when the target month has it, else clamp to its last day.
  const last = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
  target.setDate(Math.min(d.getDate(), last));
  return dayKey(target);
}

/** 0 = Sunday … 6 = Saturday: the locale's first weekday (Sunday for en-US, Monday for most of Europe). */
export function weekStartsOn(locale: string = typeof navigator !== 'undefined' ? navigator.language : 'en-US'): number {
  try {
    const loc = new Intl.Locale(locale) as Intl.Locale & {
      weekInfo?: { firstDay: number };
      getWeekInfo?: () => { firstDay: number };
    };
    const info = loc.getWeekInfo?.() ?? loc.weekInfo;
    if (info && typeof info.firstDay === 'number') return info.firstDay % 7;
  } catch {
    /* older engines: fall through */
  }
  return /^en-(US|CA)|^ja|^ko|^zh|^he|^pt-BR/i.test(locale) ? 0 : 1;
}

export function startOfWeek(key: string, firstDay: number): string {
  const d = parseDay(key);
  const back = (d.getDay() - firstDay + 7) % 7;
  return addDays(key, -back);
}

/** The weeks shown for a month: from the week holding the 1st to the week holding the last day. */
export function monthGrid(key: string, firstDay: number): string[][] {
  const d = parseDay(key);
  const first = dayKey(new Date(d.getFullYear(), d.getMonth(), 1));
  const last = dayKey(new Date(d.getFullYear(), d.getMonth() + 1, 0));
  const weeks: string[][] = [];
  let cursor = startOfWeek(first, firstDay);
  while (cursor <= last) {
    const week: string[] = [];
    for (let i = 0; i < 7; i++) week.push(addDays(cursor, i));
    weeks.push(week);
    cursor = addDays(cursor, 7);
  }
  return weeks;
}

/** The days a view covers, first to last. */
export function viewDays(view: CalView, key: string, firstDay: number): string[] {
  if (view === 'day') return [key];
  if (view === 'week') {
    const s = startOfWeek(key, firstDay);
    return Array.from({ length: 7 }, (_, i) => addDays(s, i));
  }
  return monthGrid(key, firstDay).flat();
}

/** Every local day an item touches, start to end inclusive (capped at 62 days). */
export function spanDays(startsAt: string, endsAt: string | null, allDay: boolean): string[] {
  const start = new Date(startsAt);
  const first = dayKey(start);
  if (!endsAt) return [first];
  const end = new Date(endsAt);
  // A timed event ending exactly at midnight does not touch the next day.
  const endMs = !allDay && end.getHours() === 0 && end.getMinutes() === 0 && end > start ? end.getTime() - 1 : end.getTime();
  const lastKey = dayKey(new Date(Math.max(endMs, start.getTime())));
  const out = [first];
  let k = first;
  while (k < lastKey && out.length < 62) {
    k = addDays(k, 1);
    out.push(k);
  }
  return out;
}

/** Minutes since local midnight. */
export function minutesOf(d: Date): number {
  return d.getHours() * 60 + d.getMinutes();
}

/**
 * Side-by-side lanes for overlapping timed blocks: each block gets a lane
 * index and the number of lanes in its cluster.
 */
export function layLanes<T extends { start: number; end: number }>(blocks: T[]): (T & { lane: number; lanes: number })[] {
  const sorted = [...blocks].sort((a, b) => a.start - b.start || b.end - a.end);
  const out: (T & { lane: number; lanes: number })[] = [];
  let cluster: (T & { lane: number; lanes: number })[] = [];
  let clusterEnd = -Infinity;
  const close = (): void => {
    const n = Math.max(1, ...cluster.map((c) => c.lane + 1));
    for (const c of cluster) c.lanes = n;
    cluster = [];
  };
  for (const b of sorted) {
    if (b.start >= clusterEnd && cluster.length) close();
    const used = new Set(cluster.filter((c) => c.end > b.start).map((c) => c.lane));
    let lane = 0;
    while (used.has(lane)) lane++;
    const placed = { ...b, lane, lanes: 1 };
    cluster.push(placed);
    out.push(placed);
    clusterEnd = Math.max(clusterEnd, b.end);
  }
  if (cluster.length) close();
  return out;
}

/** "October 2026" / "Oct 12 – 18, 2026" / "Thursday, October 15". */
export function viewTitle(view: CalView, key: string, firstDay: number): string {
  const d = parseDay(key);
  if (view === 'month') return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  if (view === 'day') return d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
  const days = viewDays('week', key, firstDay);
  const a = parseDay(days[0]!);
  const b = parseDay(days[6]!);
  const sameMonth = a.getMonth() === b.getMonth();
  const left = a.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  const right = b.toLocaleDateString(undefined, sameMonth ? { day: 'numeric' } : { month: 'short', day: 'numeric' });
  return `${left} – ${right}, ${b.getFullYear()}`;
}

/** 'HH:MM' for a time input, from an ISO instant (local). */
export function hhmm(iso: string): string {
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Local day + 'HH:MM' → ISO instant. */
export function localIso(key: string, time: string): string {
  const d = parseDay(key);
  const [h, m] = time.split(':').map(Number);
  d.setHours(h ?? 0, m ?? 0, 0, 0);
  return d.toISOString();
}

export function timeLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}
